import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, RefreshCw, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, Switch } from '@/components/ui/misc';
import { ApiRequestError, apiDelete, apiGet, apiPatch, apiPost } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { useFields } from './records';

type Rule = { id: string; name: string; kind: 'FIT' | 'ENGAGEMENT'; field: string; op: string; value: unknown; points: number; isActive: boolean };

const OPS = [
  ['is_not_empty', 'is known'],
  ['is_empty', 'is unknown'],
  ['eq', 'is'],
  ['neq', 'is not'],
  ['contains', 'contains'],
  ['in', 'is any of'],
  ['gte', 'is at least'],
  ['lte', 'is at most'],
  ['gt', 'is greater than'],
  ['is_true', 'is yes'],
  ['in_last_days', 'in the last (days)'],
] as const;

const failed = (error: unknown, fallback: string) => toast.error(error instanceof ApiRequestError ? error.message : fallback);

/** Settings → Lead scoring: points for who a lead is and what they did. */
export function LeadScoringPage() {
  const t = useT();
  const queryClient = useQueryClient();
  const { data } = useQuery({ queryKey: ['lead-scoring'], queryFn: () => apiGet<{ rules: Rule[]; engagementFields: { key: string; label: string }[] }>('/lead-scoring') });
  const { data: fields } = useFields('lead');
  const [draft, setDraft] = useState({ name: '', kind: 'FIT' as Rule['kind'], field: 'email', op: 'is_not_empty', value: '', points: 10 });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['lead-scoring'] });
  const add = useMutation({
    mutationFn: () =>
      apiPost('/lead-scoring', {
        ...draft,
        name: draft.name || `${draft.field} ${draft.op}`,
        value: draft.op === 'in' ? draft.value.split(',').map((item) => item.trim()).filter(Boolean) : ['gte', 'lte', 'gt', 'in_last_days'].includes(draft.op) ? Number(draft.value) : draft.value || null,
      }),
    onSuccess: () => {
      refresh();
      setDraft({ ...draft, name: '', value: '' });
    },
    onError: (error) => failed(error, 'Could not add the rule'),
  });
  const toggle = useMutation({ mutationFn: (rule: Rule) => apiPatch(`/lead-scoring/${rule.id}`, { isActive: !rule.isActive }), onSuccess: refresh });
  const remove = useMutation({ mutationFn: (id: string) => apiDelete(`/lead-scoring/${id}`), onSuccess: refresh });
  const recalc = useMutation({
    mutationFn: () => apiPost<{ scored: number }>('/lead-scoring/recalculate', {}),
    onSuccess: (result) => {
      toast.success(t('{count} leads scored again', { count: result.scored }));
      void queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
    onError: (error) => failed(error, 'Could not recalculate'),
  });
  const fieldOptions = [
    ...(fields?.builtIn ?? []).map((field) => ({ key: field.key, label: field.label })),
    ...(fields?.custom ?? []).map((field) => ({ key: field.key, label: field.label })),
    ...(data?.engagementFields ?? []),
  ];
  const labelOf = (key: string) => fieldOptions.find((field) => field.key === key)?.label ?? key;
  const rules = data?.rules ?? [];
  const noValue = ['is_not_empty', 'is_empty', 'is_true'].includes(draft.op);
  return (
    <div className="max-w-4xl">
      <PageHeader
        title="Lead scoring"
        description="Give points for who a lead is (fit) and what they have done (engagement). The score, 0–100, shows on every lead. With no rules, DovoCRM's built-in score is used."
        actions={
          <Can permission="admin.customfield.update">
            <Button variant="outline" onClick={() => recalc.mutate()} disabled={recalc.isPending}>
              <RefreshCw className={cn(recalc.isPending && 'animate-spin')} /> {t('Score all leads again')}
            </Button>
          </Can>
        }
      />
      <Can permission="admin.customfield.update">
        <div className="mb-4 grid gap-2 rounded-lg border bg-card p-4 md:grid-cols-[7rem_1fr_9rem_1fr_5rem_auto]">
          <Select value={draft.kind} onChange={(event) => setDraft({ ...draft, kind: event.target.value as Rule['kind'] })}>
            <option value="FIT">{t('Fit')}</option>
            <option value="ENGAGEMENT">{t('Engagement')}</option>
          </Select>
          <Select value={draft.field} onChange={(event) => setDraft({ ...draft, field: event.target.value })}>
            {fieldOptions.map((field) => (
              <option key={field.key} value={field.key}>
                {t(field.label)}
              </option>
            ))}
          </Select>
          <Select value={draft.op} onChange={(event) => setDraft({ ...draft, op: event.target.value })}>
            {OPS.map(([value, label]) => (
              <option key={value} value={value}>
                {t(label)}
              </option>
            ))}
          </Select>
          <Input value={draft.value} disabled={noValue} onChange={(event) => setDraft({ ...draft, value: event.target.value })} placeholder={noValue ? '' : t('Value')} />
          <Input type="number" value={draft.points} onChange={(event) => setDraft({ ...draft, points: Number(event.target.value) })} title={t('Points')} />
          <Button onClick={() => add.mutate()} disabled={add.isPending}>
            <Plus /> {t('Add')}
          </Button>
        </div>
      </Can>
      <div className="overflow-hidden rounded-lg border bg-card">
        {rules.length === 0 ? (
          <p className="p-8 text-center text-sm text-muted-foreground">{t('No rules yet — the built-in score is in use (contact details, budget, source, status).')}</p>
        ) : (
          rules.map((rule) => (
            <div key={rule.id} className={cn('flex items-center gap-3 border-b px-4 py-3 text-sm last:border-0', !rule.isActive && 'opacity-50')}>
              <Badge variant={rule.kind === 'FIT' ? 'secondary' : 'default'}>{rule.kind === 'FIT' ? t('Fit') : t('Engagement')}</Badge>
              <span className="min-w-0 flex-1">
                <span className="font-semibold">{t(labelOf(rule.field))}</span> {t(OPS.find(([value]) => value === rule.op)?.[1] ?? rule.op)}{' '}
                {rule.value !== null && rule.value !== undefined && rule.value !== '' && <span className="font-semibold">{Array.isArray(rule.value) ? rule.value.join(', ') : String(rule.value)}</span>}
              </span>
              <span className={cn('tabular w-12 text-end font-bold', rule.points >= 0 ? 'text-emerald-600' : 'text-destructive')}>{rule.points > 0 ? `+${rule.points}` : rule.points}</span>
              <Can permission="admin.customfield.update">
                <Switch checked={rule.isActive} onCheckedChange={() => toggle.mutate(rule)} />
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove.mutate(rule.id)}>
                  <Trash2 />
                </Button>
              </Can>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
